import PageLoading from '@/components/PageLoading';
import { LOCALES, LOCALE_KEY } from '@/constants';
import { ConfigProvider, Spin } from 'antd';
import dayjs from 'dayjs';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  useTransition,
  type PropsWithChildren,
} from 'react';
import { I18nContext, type IntlShape, type LocaleKey } from './context';
import {
  loadLocaleResources,
  reloadLocaleResources,
  type LocaleBundle,
} from './locale-resources';
import { bindIntl, createIntl } from './runtime';
import StartupFailure from './StartupFailure';

// 只导出组件，hook 放在 ./context 里，避免 Fast Refresh 失效
export { getMessages, type Locale } from './component-messages';
export * from './context';

/**
 * 繁体的 locale key 从 `zh-TW` 改成 `zh-HK`（2026-08-19）。业务主体是香港机构，
 * 后端的业务数据三语（`notice.i18n` 等 jsonb）与 PRD 种子用的一直是 `zh-HK`，
 * 前端跟着对齐，两侧才是同一个键。
 */
const LEGACY_LOCALE: Record<string, LocaleKey> = {
  'zh-TW': 'zh-HK',
  // 简体只保留业务内容译文，不再沿用旧版后台界面偏好。
  'zh-CN': 'zh-HK',
};

/**
 * 没选过语言时用哪一种——**繁體**（2026-08-19）。
 *
 * <p>业务主体是香港机构，全链路的默认值本来就是繁體：后端 `I18nConfig` 的
 * `defaultLocale` 是 `TRADITIONAL_CHINESE`、`tenant.default_lang` 默认 `zh-HK`、
 * PRD 的种子数据也以繁體为准。此前只有前端兜底到简体，是这条链上唯一的例外。
 */
const DEFAULT_LOCALE: LocaleKey = 'zh-HK';

function getInitialLocale(): LocaleKey {
  const raw = localStorage.getItem(LOCALE_KEY);
  // 迁移旧语言键，避免已有偏好因键名变更而失效。
  const migrated = raw ? LEGACY_LOCALE[raw] : undefined;
  if (migrated) {
    // 顺手写回，让存储值收敛——否则旧值会一直躺在那里，每次启动都要再映射一次
    localStorage.setItem(LOCALE_KEY, migrated);
  }
  const saved = migrated ?? (raw as LocaleKey | null);
  return saved && LOCALES.some(({ value }) => value === saved)
    ? saved
    : DEFAULT_LOCALE;
}

export function I18nProvider({ children }: PropsWithChildren) {
  const [locale, setLocaleState] = useState<LocaleKey>(getInitialLocale);
  const [bundle, setBundle] = useState<LocaleBundle>();
  const [failure, setFailure] = useState<{ error: unknown }>();
  const [isPending, startTransition] = useTransition();

  useEffect(() => {
    // index.html 里的 lang 是写死的，不同步的话读屏软件与 :lang() 选择器
    // 拿到的永远是首屏那一种语言
    document.documentElement.lang = locale;
  }, [locale]);

  useEffect(() => {
    let alive = true;
    setFailure(undefined);
    loadLocaleResources(locale)
      .then((next) => {
        if (!alive) return;
        dayjs.locale(
          locale === 'zh-HK' ? 'zh-hk' : locale === 'zh-CN' ? 'zh-cn' : 'en',
        );
        startTransition(() => setBundle(next));
      })
      .catch((error: unknown) => {
        if (alive) setFailure({ error });
      });
    return () => {
      alive = false;
    };
  }, [locale]);

  const setLocale = useCallback((next: LocaleKey) => {
    localStorage.setItem(LOCALE_KEY, next);
    setLocaleState(next);
  }, []);

  // 用 bundle 里的 key：切换语言时先保持旧文案，等新语言包到了再整体切换，避免闪烁
  const intl = useMemo<IntlShape>(
    () => createIntl(bundle?.key ?? locale, bundle?.messages ?? {}),
    [bundle, locale],
  );

  // 请求拦截器与 store 在 React 之外，出错提示从这里登记的那一份取词条（i18n/runtime.ts 的 translate）。
  // 语言包到了才登记：子组件发出的请求最早也要等响应回来才用到它，那时这一步早已跑完
  useLayoutEffect(() => {
    if (bundle) bindIntl(intl);
  }, [bundle, intl]);

  const value = useMemo(() => ({ intl, setLocale }), [intl, setLocale]);

  // 首屏等第一个语言包（一个小 chunk）到达再渲染，避免闪出未翻译的 key
  if (!bundle)
    return failure ? (
      <StartupFailure locale={locale} onRetry={reloadLocaleResources} />
    ) : (
      <PageLoading />
    );

  return (
    <I18nContext.Provider value={value}>
      <ConfigProvider locale={bundle.antd}>
        <Spin
          fullscreen
          spinning={!failure && (bundle.key !== locale || isPending)}
        />
        {failure ? (
          <StartupFailure locale={locale} onRetry={reloadLocaleResources} />
        ) : null}
        {/* 加载失败时保留路由实例，避免卸载正在进行的页面操作。 */}
        <div style={{ display: failure ? 'none' : 'contents' }}>{children}</div>
      </ConfigProvider>
    </I18nContext.Provider>
  );
}
